import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid'
import { observer } from 'mobx-react'
import React, { useContext } from 'react'
import { Link } from 'react-router-dom'

import { AnnouncementStoreContext } from '@/stores/AnnouncementStore'

/**
 * Per-level styling. Uses color tokens that actually resolve in the Tailwind
 * config (info→blue, warning→gold, success/error have dedicated tokens).
 */
const LEVEL_CONFIG = {
  info: {
    icon: InformationCircleIcon,
    wrapper: 'bg-blue-50 border-blue-500',
    iconColor: 'text-blue-600',
    title: 'text-blue-900',
    text: 'text-blue-800',
  },
  success: {
    icon: CheckCircleIcon,
    wrapper: 'bg-success-light border-success',
    iconColor: 'text-success',
    title: 'text-success-dark',
    text: 'text-success-dark',
  },
  warning: {
    icon: ExclamationTriangleIcon,
    wrapper: 'bg-gold-100 border-gold-500',
    iconColor: 'text-gold-700',
    title: 'text-gold-900',
    text: 'text-gold-800',
  },
  error: {
    icon: XCircleIcon,
    wrapper: 'bg-error-light border-error',
    iconColor: 'text-error',
    title: 'text-error-dark',
    text: 'text-error-dark',
  },
}

const isInternalLink = (url) => typeof url === 'string' && url.startsWith('/')

const AnnouncementRow = observer(({ announcement }) => {
  const store = useContext(AnnouncementStoreContext)
  const config = LEVEL_CONFIG[announcement.level] || LEVEL_CONFIG.info
  const Icon = config.icon

  const renderCta = () => {
    const className = `text-sm font-semibold underline underline-offset-2 ${config.title}`

    // Long-form content takes precedence: the banner button opens the modal.
    // Any configured link is surfaced as a button *inside* the modal (see
    // AnnouncementDetailModal), so linkLabel is not used as the modal trigger.
    if (announcement.hasBody) {
      return (
        <button
          type="button"
          onClick={() => store.openDetail(announcement.id)}
          className={className}
        >
          Read more
        </button>
      )
    }

    // No modal content: the link is the banner's call-to-action. Mark the
    // announcement read on click so its bell unread badge clears once the user
    // has engaged from the banner.
    if (announcement.linkUrl) {
      const label = announcement.linkLabel || 'Learn more'
      const markRead = () => store.markRead(announcement.id)
      if (isInternalLink(announcement.linkUrl)) {
        return (
          <Link to={announcement.linkUrl} onClick={markRead} className={className}>
            {label}
          </Link>
        )
      }
      return (
        <a
          href={announcement.linkUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={markRead}
          className={className}
        >
          {label}
        </a>
      )
    }

    return null
  }

  return (
    <div
      className={`flex items-start gap-3 border-b px-4 py-3 sm:px-6 lg:px-8 ${config.wrapper}`}
      role="status"
    >
      <Icon className={`mt-0.5 h-5 w-5 flex-none ${config.iconColor}`} aria-hidden="true" />

      <div className="flex flex-1 flex-col gap-0.5 sm:flex-row sm:items-baseline sm:gap-2">
        <span className={`text-sm font-semibold ${config.title}`}>{announcement.title}</span>
        <span className={`text-sm ${config.text}`}>{announcement.message}</span>
        <span className="sm:ml-1">{renderCta()}</span>
      </div>

      {announcement.isDismissable && (
        <button
          type="button"
          onClick={() => store.dismiss(announcement.id)}
          aria-label="Dismiss announcement"
          className="flex-none"
        >
          <XMarkIcon className={`h-5 w-5 ${config.text}`} />
        </button>
      )}
    </div>
  )
})

/**
 * AnnouncementBanner
 *
 * Full-width, stacked site-wide announcement banners rendered below the nav bar.
 * Shows every active, non-dismissed announcement flagged `show_as_banner` for
 * the current user. Non-dismissable announcements omit the close button.
 */
const AnnouncementBanner = observer(() => {
  const store = useContext(AnnouncementStoreContext)
  const banners = store.bannerAnnouncements

  if (!banners.length) return null

  return (
    <div className="w-full">
      {banners.map((announcement) => (
        <AnnouncementRow key={announcement.id} announcement={announcement} />
      ))}
    </div>
  )
})

export default AnnouncementBanner
